import { safeSetDoc, safeUpdateDoc, safeDeleteDoc, safeOnSnapshot, safeWriteBatch } from '../utils';
import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, doc } from 'firebase/firestore';

export function useFilmTVData(uid) {
  const [movies, setMovies] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setMovies([]);
      setTvShows([]);
      setCreators([]);
      setLoading(false);
      return;
    }

    const moviesRef = collection(db, `users/${uid}/filmTrackerMovies`);
    const tvRef = collection(db, `users/${uid}/filmTrackerTv`);
    const creatorsRef = collection(db, `users/${uid}/filmTrackerCreators`);

    const unsubMovies = safeOnSnapshot(query(moviesRef), (snap) => {
      setMovies(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.position || 0) - (b.position || 0)));
    });

    const unsubTv = safeOnSnapshot(query(tvRef), (snap) => {
      setTvShows(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.position || 0) - (b.position || 0)));
    });

    const unsubCreators = safeOnSnapshot(query(creatorsRef), (snap) => {
      setCreators(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a, b) => (a.position || 0) - (b.position || 0)));
    });

    setLoading(false);

    return () => {
      unsubMovies();
      unsubTv();
      unsubCreators();
    };
  }, [uid]);

  const addMovie = async (movieData) => {
    if (!uid) return;
    const docRef = doc(collection(db, `users/${uid}/filmTrackerMovies`), movieData.tmdbId.toString());
    await safeSetDoc(docRef, {
      ...movieData,
      addedAt: new Date().toISOString(),
      seen: false,
      toWatch: true,
      flagged: false,
      notInterested: false,
      notes: '',
      watchedDate: null,
      position: Date.now()
    });
  };

  const updateMovie = async (id, data) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerMovies`, id.toString());
    await safeUpdateDoc(docRef, data);
  };

  const deleteMovie = async (id) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerMovies`, id.toString());
    await safeDeleteDoc(docRef);
  };

  const addTvShow = async (tvData) => {
    if (!uid) return;
    const docRef = doc(collection(db, `users/${uid}/filmTrackerTv`), tvData.tmdbId.toString());
    await safeSetDoc(docRef, {
      ...tvData,
      addedAt: new Date().toISOString(),
      seen: false,
      toWatch: true,
      flagged: false,
      notInterested: false,
      notes: '',
      watchedDate: null,
      position: Date.now()
    });
  };

  const updateTvShow = async (id, data) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerTv`, id.toString());
    await safeUpdateDoc(docRef, data);
  };

  const deleteTvShow = async (id) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerTv`, id.toString());
    await safeDeleteDoc(docRef);
  };

  const addCreator = async (creatorData) => {
    if (!uid) return;
    const docRef = doc(collection(db, `users/${uid}/filmTrackerCreators`), creatorData.id.toString());
    await safeSetDoc(docRef, {
      ...creatorData,
      addedAt: new Date().toISOString(),
      position: Date.now()
    });
  };

  const updateCreator = async (id, data) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerCreators`, id.toString());
    await safeUpdateDoc(docRef, data);
  };

  const deleteCreator = async (id) => {
    if (!uid) return;
    const docRef = doc(db, `users/${uid}/filmTrackerCreators`, id.toString());
    await safeDeleteDoc(docRef);
  };

  const importData = async (jsonData) => {
    if (!uid || !jsonData) return;
    const batch = safeWriteBatch(db);

    if (jsonData.tvShows && Array.isArray(jsonData.tvShows)) {
      jsonData.tvShows.forEach((show) => {
        if (!show.tmdbId) return;
        const ref = doc(db, `users/${uid}/filmTrackerTv`, show.tmdbId.toString());
        batch.set(ref, {
          ...show,
          position: show.position || Date.now(),
          addedAt: show.addedAt || new Date().toISOString()
        }, { merge: true });
      });
    }

    if (jsonData.creators && Array.isArray(jsonData.creators)) {
      jsonData.creators.forEach((creator) => {
        if (!creator.id && !creator.tmdbId) return;
        // Check if it's the "My Added Films" meta-creator from the legacy app
        if (creator.name === "My Added Films" || creator.name === "My Standalone Films") {
          if (Array.isArray(creator.movies)) {
            creator.movies.forEach((m) => {
              if (!m.tmdbId) return;
              const ref = doc(db, `users/${uid}/filmTrackerMovies`, m.tmdbId.toString());
              batch.set(ref, {
                ...m,
                position: m.position || Date.now(),
                addedAt: m.addedAt || new Date().toISOString()
              }, { merge: true });
            });
          }
        } else {
          // Normal creator
          const cid = (creator.id || creator.tmdbId).toString();
          const ref = doc(db, `users/${uid}/filmTrackerCreators`, cid);
          batch.set(ref, {
            ...creator,
            id: cid,
            position: creator.position || Date.now(),
            addedAt: creator.addedAt || new Date().toISOString()
          }, { merge: true });
        }
      });
    }

    await batch.commit();
  };

  return {
    movies, tvShows, creators, loading,
    addMovie, updateMovie, deleteMovie,
    addTvShow, updateTvShow, deleteTvShow,
    addCreator, updateCreator, deleteCreator,
    importData
  };
}
